<script setup lang="ts">
import { computed, ref, watch } from "vue";

import AlmLinkChip from "@/components/chip/AlmLinkChip.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import ListItem from "@/components/ui/ComplexList/ListItem.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import UserAvatar from "@/components/ui/UserAvatar/UserAvatar.vue";
import { useUser } from "@/composables/useUser";
import type { IssueSelectorCard } from "@/model/issueSelector";
import type { LinkedToItem } from "@/store/issueSelector";
import { useIssueSelectorStore } from "@/store/issueSelector";
import { useLinkStore } from "@/store/link";
import { useTeamStore } from "@/store/team";

import type { DragStickyOptions } from "../useDragToCreateSticky";
import { useDragToCreateSticky } from "../useDragToCreateSticky";
import { highlightLinkedCards } from "./utils";

const props = defineProps<{ issue: IssueSelectorCard }>();

const { userName } = useUser();

const isBacklogItem = computed(() => props.issue.type.origin === "backlog");

const showAddToFilter = computed(
  () => isBacklogItem.value && !!props.issue.almId && useIssueSelectorStore().showAlmItemsSection,
);

/**
 * True if the given backlog item is currently in the linked-to filter
 */
const activeInFilter = computed(
  () => !!props.issue.almId && useIssueSelectorStore().linkedToAlmIds.includes(props.issue.almId),
);

/**
 * Determines if we're in drag-to-mirror mode (for backlog items)
 * or drag-to-create mode (for ALM Issues)
 */
const dragOptions = computed<DragStickyOptions>(() => {
  const isBacklogItem = !!props.issue.id;

  return isBacklogItem
    ? {
        mode: "mirror",
        cardProps: { id: props.issue.id!, type: props.issue.type },
        trackingLabel: "backlog-selector-mirror",
        parentSelector: ".issue-selector-panel",
        autoFocus: false,
      }
    : {
        mode: "create",
        getCardProps: (dragStart) => ({
          pos: dragStart,
          type: props.issue.type,
          text: props.issue.text,
          almId: props.issue.almId,
          almSourceId: props.issue.almSourceId || undefined,
          storyPoints: props.issue.points,
        }),
        trackingLabel: "issue-selector-drag",
        parentSelector: ".issue-selector-panel",
        autoFocus: false,
      };
});

const { handlePointerDown, isDragging } = useDragToCreateSticky(dragOptions.value);

// Prevent clicks after drag - isDragging resets before click event fires
const wasDragged = ref(false);
watch(isDragging, (v) => v && (wasDragged.value = true));

const suppressIfDragged = (e: MouseEvent) => {
  if (wasDragged.value) {
    wasDragged.value = false;
    e.preventDefault();
  }
};

/**
 * Toggle this backlog item in/out of the linked-to filter.
 * Supports multi-select: clicking adds/removes items from the selection.
 */
const addToFilter = () => {
  if (!props.issue.almId || !props.issue.id) return;
  if (wasDragged.value) {
    wasDragged.value = false;
    return;
  }

  const store = useIssueSelectorStore();
  const isSelected = store.linkedToAlmIds.includes(props.issue.almId);

  if (isSelected) {
    // Remove from selection
    const newItems = store.selectedLinkedToItems.filter((item) => item.almId !== props.issue.almId);
    store.setSelectedLinkedToItems(newItems);
    void store.fetchIssues();

    if (newItems.length === 0) {
      useLinkStore().removeAllMarks();
    } else {
      const cardIds = newItems.map((item) => item.id).filter((id): id is string => !!id);
      void highlightLinkedCards(cardIds);
    }
  } else {
    // Add to selection
    const team = useTeamStore().current;
    const item: LinkedToItem = {
      id: props.issue.id,
      almId: props.issue.almId,
      text: props.issue.text,
      type: props.issue.type,
      teamName: team.name,
      teamId: team.id,
    };

    const newItems = [...store.selectedLinkedToItems, item];
    store.setSelectedLinkedToItems(newItems);
    void store.fetchIssues();

    const cardIds = newItems.map((i) => i.id).filter((id): id is string => !!id);
    void highlightLinkedCards(cardIds);
  }
};
</script>

<template>
  <ListItem
    class="issue-list-item draggable"
    :class="{ 'is-dragging': isDragging }"
    compact
    @pointerdown="handlePointerDown"
  >
    <div class="issue-list-item-content">
      <TypeDot :color="issue.type.color" size="medium" />

      <AlmLinkChip
        v-if="issue.almIssueUrl && issue.almId"
        :text="issue.almId"
        :link="issue.almIssueUrl"
        @click="suppressIfDragged"
      />

      <div class="issue-summary-container">
        <BaseTooltip position="top">
          <div class="issue-summary">{{ issue.text }}</div>
          <template #content>{{ issue.text }}</template>
        </BaseTooltip>
      </div>
      <BaseTooltip v-if="issue.assignee">
        <template #content>{{ userName(issue.assignee) }}</template>
        <UserAvatar :user="issue.assignee" class="assignee" />
      </BaseTooltip>
      <IconButton
        v-if="showAddToFilter"
        icon="general/external-links"
        size="small"
        class="add-to-filter-button"
        :activated="activeInFilter"
        :text="$t('label.showLinkedWorkItems')"
        @click="addToFilter"
      />
      <span class="story-points">{{ issue.points }}</span>
    </div>
  </ListItem>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";

:deep(.issue-list-item) {
  @include typography.medium("semi-bold");

  width: 100%;
  user-select: none;
  cursor: grab;

  &.is-dragging {
    position: relative; // Allows it to move during drag
    opacity: 0.4;
  }

  .content .title {
    height: unset;
  }

  .issue-list-item-content {
    width: 100%;
    display: flex;
    gap: 8px;
    align-items: center;

    .issue-summary-container {
      flex: 1;
      min-width: 0;
      height: 100%;
      display: flex;

      .issue-summary {
        overflow-x: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .assignee {
      line-height: 100%;
    }

    .story-points {
      flex-shrink: 0;
      padding: 2px 6px;
      color: colors.$text-subtle;
      line-height: 100%;
    }

    .add-to-filter-button {
      height: 28px;
      width: 28px;
      padding: 4px;
    }
  }

  &.active .story-points {
    color: unset;
  }
}
</style>
